// Helpers puros das telas de Orçamento e Relatórios (Fase 5): top N + "Outras", variação
// percentual e cor por situação do orçamento. Sem DOM, testados em reportUtils.test.ts.
import type { BudgetStatus } from '@/types/finance';

export interface NamedAmount {
  name: string;
  /** Nome da paleta (utils/colors.ts) ou null. */
  color?: string | null;
  amount_cents: number;
}

export const OTHERS_LABEL = 'Outras';

/**
 * Mantém as `n` maiores (por `amount_cents`, desc) e agrega o resto numa linha "Outras" (cor
 * `slate`). Com `n + 1` itens ou menos, devolve a lista ordenada sem agregar (agregar um só
 * item em "Outras" esconderia o nome à toa). Zeros e negativos ficam de fora.
 */
export const topNWithOthers = <T extends NamedAmount>(items: T[], n: number): (T | NamedAmount)[] => {
  const sorted = items.filter((i) => i.amount_cents > 0).sort((a, b) => b.amount_cents - a.amount_cents);
  if (n <= 0 || sorted.length <= n + 1) return sorted;
  const head = sorted.slice(0, n);
  const rest = sorted.slice(n).reduce((sum, i) => sum + i.amount_cents, 0);
  return [...head, { name: OTHERS_LABEL, color: 'slate', amount_cents: rest }];
};

/**
 * Variação percentual de `current` sobre `prev` (1 casa): 1250 vs 1000 → 25. `null` quando
 * `prev` é zero (não há base de comparação).
 */
export const pctChange = (current: number, prev: number): number | null => {
  if (!prev) return null;
  return Math.round(((current - prev) / Math.abs(prev)) * 1000) / 10;
};

/** "+25,0%" / "−8,3%" / "0,0%"; "—" para null. */
export const formatPctChange = (pct: number | null): string => {
  if (pct === null || !Number.isFinite(pct)) return '—';
  const sign = pct > 0 ? '+' : pct < 0 ? '−' : '';
  return `${sign}${Math.abs(pct).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
};

/** "12,5%" (1 casa). */
export const formatPct = (pct?: number | null): string =>
  pct === null || pct === undefined || !Number.isFinite(pct) ? '—' : `${pct.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

export interface BudgetStatusColor {
  /** Barra de progresso. */
  bar: string;
  /** Texto do percentual. */
  text: string;
  /** Badge (fundo + texto). */
  badge: string;
}

const BUDGET_STATUS_COLORS: Record<BudgetStatus, BudgetStatusColor> = {
  none: { bar: 'bg-slate-300', text: 'text-slate-500', badge: 'bg-slate-100 text-slate-600' },
  ok: { bar: 'bg-emerald-500', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700' },
  warning: { bar: 'bg-amber-500', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700' },
  exceeded: { bar: 'bg-red-500', text: 'text-red-700', badge: 'bg-red-100 text-red-700' },
};

/** Classes Tailwind por situação do orçamento: ok verde · warning âmbar · exceeded vermelho · none cinza. */
export const budgetStatusColor = (status: BudgetStatus | undefined | null): BudgetStatusColor => BUDGET_STATUS_COLORS[status ?? 'none'] ?? BUDGET_STATUS_COLORS.none;

/** Situação pelo percentual (espelho da regra do backend: ≥ 80 % atenção, > 100 % estourado). */
export const budgetStatusOf = (spent: number, budget?: number | null): BudgetStatus => {
  if (!budget || budget <= 0) return 'none';
  const pct = (spent / budget) * 100;
  if (pct > 100) return 'exceeded';
  if (pct >= 80) return 'warning';
  return 'ok';
};

/** Percentual limitado a 0..100 para a largura da barra. */
export const clampPct = (pct?: number | null): number => Math.max(0, Math.min(100, Number.isFinite(pct ?? NaN) ? (pct as number) : 0));
